<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, ref, watch } from 'vue'
  import {
    type CustomerCase as Case,
    type CustomerEntity,
    type CustomerEntityWithRelationship as EntityWithRelationship
  } from '@/types/generated'
  import Entity from '@/components/app/cases/entities/Entity.vue'
  import {
    PlusIcon,
    User,
    Building2,
    Landmark,
    MoreVertical,
    Trash2,
    Check,
    Minus
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { RelationshipTypeEnum, EntityTypeEnum } from '@/types/generated'
  import { useRoute } from 'vue-router'

  import { entitiesDE, entitiesEN } from '@/i18n/entity'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'

  const { t } = useTranslation({
    en: {
      parties: 'Parties',
      addNewParty: 'Add new party',
      selectParty: 'Select a party from the list',
      selectAll: 'Select All',
      deselectAll: 'Deselect All',
      selectAllEntities: 'Select all entities',
      deselectAllEntities: 'Deselect all entities',
      ...Object.fromEntries(Object.entries(entitiesEN).map(([key, value]) => [key, value])),
      unnamed: 'Unnamed',
      notEnabledInExport: 'Not enabled in this export',
      delete_party: 'Delete party',
      confirm_delete: 'Confirm deletion',
      confirm_delete_party_message:
        'Are you sure you want to delete this party? This action cannot be undone.',
      cancel: 'Cancel',
      delete: 'Delete'
    },
    de: {
      parties: 'Beteiligte',
      addNewParty: 'Beteiligten hinzufügen',
      selectParty: 'Wählen Sie einen Beteiligten aus der Liste aus',
      selectAll: 'Alle auswählen',
      deselectAll: 'Alle abwählen',
      selectAllEntities: 'Alle Beteiligte auswählen',
      deselectAllEntities: 'Alle Beteiligte abwählen',
      ...Object.fromEntries(Object.entries(entitiesDE).map(([key, value]) => [key, value])),
      unnamed: 'Unbenannt',
      notEnabledInExport: 'In diesem Export nicht verfügbar',
      delete_party: 'Beteiligten löschen',
      confirm_delete: 'Löschen bestätigen',
      confirm_delete_party_message:
        'Sind Sie sicher, dass Sie diesen Beteiligten löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      delete: 'Löschen'
    }
  })

  const emit = defineEmits(['update:selected-entities-relationships'])

  const props = defineProps({
    unifiedExportMode: {
      type: Boolean,
      required: false,
      default: false
    },
    insuranceInfoEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    opponentEnabled: {
      type: Boolean,
      required: false,
      default: true
    },
    miscEntitiesEnabled: {
      type: Boolean,
      required: false,
      default: true
    }
  })

  const caseStore = useCaseStore()
  const route = useRoute()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)
  const clientEntityRelationship = computed<string | null>(() => {
    const clientEntity = caseRecord.value?.data.entities.find((entity) =>
      entity.relationships.some(
        (rel) => rel.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
    )
    return clientEntity?.uuid
  })
  const selectedEntityRelationship = ref<EntityWithRelationship | null>(null)

  // Variables for delete confirmation modal
  const showDeleteConfirmation = ref(false)
  const entityToDelete = ref<EntityWithRelationship | null>(null)

  // Popover state management
  const openPopoverId = ref<string | null>(null)

  // Helper functions to check entity types
  const isClientEntity = (entity: EntityWithRelationship | undefined | null) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
    )
  }

  const isOpponentEntity = (entity: EntityWithRelationship | undefined | null) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
    )
  }

  const isPriorityEntity = (entity: EntityWithRelationship | undefined | null) => {
    if (!entity) return false
    return isClientEntity(entity) || isOpponentEntity(entity)
  }

  const isEntityEnabled = (entityWithRelationship: EntityWithRelationship) => {
    if (!entityWithRelationship) {
      return false
    }
    // if the entity is a client, then it is it enabled and selectable
    if (
      entityWithRelationship.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
      )
    ) {
      return true
    }
    // if the entity is an opponent, then the entity is dependent on
    // props.opponentEnabled
    if (
      entityWithRelationship.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
    ) {
      return props.opponentEnabled
    }
    // any other kind of (misc) entity is dependent on props.miscEntitiesEnabled
    return props.miscEntitiesEnabled
  }

  // Sort entities to ensure client is first, opponent is second, then all others
  const sortedEntities = computed(() => {
    if (!caseRecord.value?.data.entities) return []

    return [...caseRecord.value.data.entities].sort((a, b) => {
      const aIsClient = isClientEntity(a)
      const bIsClient = isClientEntity(b)
      const aIsOpponent = isOpponentEntity(a)
      const bIsOpponent = isOpponentEntity(b)

      if (aIsClient && !bIsClient) return -1
      if (!aIsClient && bIsClient) return 1
      if (aIsOpponent && !bIsOpponent && !bIsClient) return -1
      if (!aIsOpponent && bIsOpponent && !aIsClient) return 1
      return 0
    })
  })

  // Determine if we should show a separator after priority entities
  const shouldShowSeparator = computed(() => {
    // Only show separator if we have more than just client and opponent
    if (sortedEntities.value.length <= 2) return false

    // Count priority entities (client and opponent)
    const priorityCount = sortedEntities.value.filter((e) => isPriorityEntity(e)).length

    // If we have priority entities and we have more than just those, show separator
    return priorityCount > 0 && priorityCount < sortedEntities.value.length
  })

  const selectEntityRelationship = (entity: EntityWithRelationship) => {
    selectedEntityRelationship.value = entity
  }

  const addNewEntity = async () => {
    // Close any open popovers
    openPopoverId.value = null

    const newEntityRelationship = await caseStore.addEntity({
      entity_type: EntityTypeEnum.NATURAL_PERSON
    } as CustomerEntity)
    selectedEntityRelationship.value = newEntityRelationship
  }

  // Function to show the delete confirmation modal
  const confirmDeleteEntity = (entity: EntityWithRelationship) => {
    entityToDelete.value = entity
    showDeleteConfirmation.value = true

    // Close any open popover
    openPopoverId.value = null
  }

  const togglePopover = (uuid: string) => {
    openPopoverId.value = openPopoverId.value === uuid ? null : uuid
  }

  // Function to actually delete the entity after confirmation
  const deleteEntity = async () => {
    try {
      if (!entityToDelete.value || !caseRecord.value?.uuid) return

      await caseStore.deleteEntity(entityToDelete.value)

      // If the deleted entity was selected, clear the selection
      if (selectedEntityRelationship.value?.uuid === entityToDelete.value.uuid) {
        selectedEntityRelationship.value = null
      }

      // Reset the confirmation modal state
      showDeleteConfirmation.value = false
      entityToDelete.value = null
    } catch (error) {
      console.error('Error deleting entity:', error)
    }
  }

  // Auto-select entity based on URL parameter or default to client
  watch(
    [caseRecord, () => route.params.entityUuid],
    ([newCase, entityUuid]) => {
      if (newCase?.data.entities) {
        if (entityUuid) {
          // If entity UUID is provided in URL, select that entity
          const entityFromUrl = newCase.data.entities.find(
            (entity) => entity.entity.uuid === entityUuid
          )
          if (entityFromUrl) {
            selectEntityRelationship(entityFromUrl)
            return
          }
        }

        // If no entity UUID in URL or entity not found, default to client
        const clientEntity = newCase.data.entities.find((entity) =>
          entity.relationships.some(
            (rel) => rel.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
          )
        )
        if (clientEntity) {
          selectEntityRelationship(clientEntity)
        }
      }
    },
    { immediate: true }
  )

  const selectedEntitiesWithRelationships = ref<Array<string>>([])

  // Initialize with all enabled entities selected by default
  watch(
    [clientEntityRelationship, sortedEntities, () => props.unifiedExportMode],
    ([newClientUuid, entities, unifiedMode]) => {
      // Select all enabled entities by default in unified export mode
      if (entities.length > 0 && unifiedMode) {
        const allEnabledEntities = entities
          .filter((entity) => isEntityEnabled(entity))
          .map((entity) => entity.uuid)

        // Set the selected entities to all enabled entities
        selectedEntitiesWithRelationships.value = [...new Set(allEnabledEntities)]
        emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
      }
      // If not in unified export mode, just select the client
      else if (newClientUuid && !selectedEntitiesWithRelationships.value.includes(newClientUuid)) {
        selectedEntitiesWithRelationships.value.push(newClientUuid)
        emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
      }
    },
    { immediate: true }
  )

  const updateSelectedEntitiesWithRelationships = (entityUuid: string | undefined) => {
    if (!entityUuid) return

    const index = selectedEntitiesWithRelationships.value.indexOf(entityUuid)
    if (index !== -1) {
      // Don't allow deselection of client entity
      if (entityUuid === clientEntityRelationship.value) return

      selectedEntitiesWithRelationships.value.splice(index, 1)
    } else {
      selectedEntitiesWithRelationships.value.push(entityUuid)
    }

    emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
  }

  // Deselect all entities except the client entity
  const deselectAll = () => {
    // Keep only the client entity selected
    if (clientEntityRelationship.value) {
      selectedEntitiesWithRelationships.value = [clientEntityRelationship.value]
    } else {
      selectedEntitiesWithRelationships.value = []
    }
    emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
  }

  // Select all enabled entities
  const selectAll = () => {
    const allEnabledEntities = sortedEntities.value
      .filter((entity) => isEntityEnabled(entity))
      .map((entity) => entity.uuid)

    // Set the selected entities to all enabled entities
    selectedEntitiesWithRelationships.value = [...new Set(allEnabledEntities)]
    emit('update:selected-entities-relationships', selectedEntitiesWithRelationships.value)
  }

  // Check if an entity is currently selected
  const isEntitySelected = (entityUuid: string) => {
    return selectedEntitiesWithRelationships.value.includes(entityUuid)
  }

  // Check if all enabled entities are selected
  const areAllEntitiesSelected = computed(() => {
    const enabledEntities = sortedEntities.value
      .filter((entity) => isEntityEnabled(entity))
      .map((entity) => entity.uuid)

    return enabledEntities.every((uuid) => selectedEntitiesWithRelationships.value.includes(uuid))
  })

  const divClass = computed(() => {
    return props.unifiedExportMode
      ? 'rounded-xl border bg-white p-6 shadow-md'
      : 'mt-10 rounded-xl border border-gray-200 bg-white p-4 shadow-sm'
  })
</script>
<template>
  <div>
    <div :class="divClass">
      <div class="flex gap-5 pb-4">
        <div class="w-1/4 pr-4">
          <div class="text-lg font-semibold text-gray-800">
            {{ t('parties') }} ({{ selectedEntitiesWithRelationships.length }})
          </div>
        </div>
        <div class="w-3/4">
          <div class="flex items-center justify-end gap-2">
            <!-- Select All / Deselect All buttons for unified export mode -->
            <template v-if="unifiedExportMode && sortedEntities.length > 0">
              <!-- Select All button - shown when not all entities are selected -->
              <button
                v-if="
                  !areAllEntitiesSelected &&
                  sortedEntities.filter((entity) => isEntityEnabled(entity)).length >
                    selectedEntitiesWithRelationships.length
                "
                class="inline-flex items-center gap-x-2 rounded-lg border border-purple-500 bg-white px-3 py-2 text-sm
                  font-semibold text-purple-700 hover:bg-purple-50 focus:outline-none focus:ring-2
                  focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
                @click="selectAll"
              >
                <Check class="h-4 w-4" />
                {{ t('selectAllEntities') }}
              </button>
              <!-- Deselect All button - shown when entities are selected -->
              <button
                v-if="
                  selectedEntitiesWithRelationships.length >
                  (clientEntityRelationship.value ? 1 : 0)
                "
                class="inline-flex items-center gap-x-2 rounded-lg border border-purple-500 bg-purple-500 px-3 py-2 text-sm
                  font-semibold text-white hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500
                  disabled:pointer-events-none disabled:opacity-50"
                @click="deselectAll"
              >
                <Minus class="h-4 w-4" />
                {{ t('deselectAllEntities') }}
              </button>
            </template>
            <button
              class="inline-flex items-center gap-x-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500
                disabled:pointer-events-none disabled:opacity-50"
              @click="addNewEntity"
            >
              <PlusIcon class="h-5 w-5" />
              {{ t('addNewParty') }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex gap-5">
        <div class="w-1/4 border-r-2 pr-4">
          <div class="">
            <!-- Priority entities (client and opponent) -->
            <template
              v-for="entity in sortedEntities"
              :key="entity.uuid"
            >
              <!-- Only render priority entities in this first loop -->
              <div
                v-if="isPriorityEntity(entity)"
                class="flex items-center justify-between rounded-lg p-2 hover:bg-gray-50"
                :class="{
                  'bg-gray-100': selectedEntityRelationship?.uuid === entity.uuid,
                  'pointer-events-none': !isEntityEnabled(entity)
                }"
                @click="selectEntityRelationship(entity)"
              >
                <CheckboxField
                  v-if="unifiedExportMode"
                  :checked-value="true"
                  :unchecked-value="false"
                  :model-value="isEntitySelected(entity.uuid)"
                  :name="`entity-selection-${entity.uuid}`"
                  :disabled="!isEntityEnabled(entity) || isClientEntity(entity)"
                  @update:model-value="
                    (val) => updateSelectedEntitiesWithRelationships(entity.uuid)
                  "
                />
                <div
                  class="ml-0.5 flex min-w-0 flex-grow cursor-pointer items-center"
                  :class="{
                    'text-gray-600': isEntityEnabled(entity),
                    'text-gray-500': !isEntityEnabled(entity)
                  }"
                  @click="selectEntityRelationship(entity)"
                >
                  <div class="mr-3 flex flex-shrink-0 items-center justify-center">
                    <User
                      v-if="entity.entity?.entity_type === EntityTypeEnum.NATURAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Building2
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Landmark
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.INSTITUTION"
                      class="h-6 w-6 text-gray-600"
                    />
                    <User
                      v-else
                      class="h-6 w-6 text-gray-600"
                    />
                    <!-- Default to User icon if type not specified -->
                  </div>

                  <div class="min-w-0 flex-grow">
                    <div
                      class="truncate text-sm font-semibold text-gray-600"
                      :class="{
                        'text-gray-600': isEntityEnabled(entity),
                        'text-gray-500': !isEntityEnabled(entity)
                      }"
                    >
                      {{
                        entity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON
                          ? entity.entity?.company_name || t('unnamed')
                          : `${entity.entity?.title || ''} ${entity.entity?.first_name || ''} ${entity.entity?.last_name || ''}`.trim() ||
                            t('unnamed')
                      }}
                    </div>
                    <div
                      class="text-xs text-gray-500"
                      :class="{
                        'text-gray-600': isEntityEnabled(entity),
                        'text-gray-500': !isEntityEnabled(entity)
                      }"
                    >
                      <span
                        v-for="(relationship, index) in entity.relationships"
                        :key="relationship.uuid"
                        class="text-gray-600"
                      >
                        {{ t(relationship.relationship_type)
                        }}{{ index < entity.relationships.length - 1 ? ', ' : '' }}
                      </span>
                    </div>
                    <div
                      v-if="!isEntityEnabled(entity)"
                      class="text-xs text-gray-500"
                    >
                      {{ t('notEnabledInExport') }}
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Add a horizontal rule separator after priority entities -->
            <hr
              v-if="shouldShowSeparator"
              class="my-3 border-gray-200"
            />

            <!-- Non-priority entities (all other types) -->
            <template
              v-for="entity in sortedEntities"
              :key="`non-priority-${entity.uuid}`"
            >
              <!-- Only render non-priority entities in this second loop -->
              <div
                v-if="!isPriorityEntity(entity)"
                class="mb-2 flex min-h-[52px] items-center justify-between rounded-lg p-2 hover:bg-gray-50"
                :class="{
                  'bg-gray-100': selectedEntityRelationship?.uuid === entity.uuid
                }"
              >
                <CheckboxField
                  v-if="unifiedExportMode"
                  :checked-value="true"
                  :unchecked-value="false"
                  :model-value="isEntitySelected(entity.uuid)"
                  :name="`entity-selection-${entity.uuid}`"
                  :disabled="!isEntityEnabled(entity)"
                  @update:model-value="
                    (val) => updateSelectedEntitiesWithRelationships(entity.uuid)
                  "
                />
                <div
                  class="ml-0.5 flex min-w-0 flex-grow cursor-pointer items-center"
                  :class="{
                    'pointer-events-none': !isEntityEnabled(entity)
                  }"
                  @click="selectEntityRelationship(entity)"
                >
                  <div class="mr-3 flex flex-shrink-0 items-center justify-center">
                    <User
                      v-if="entity.entity?.entity_type === EntityTypeEnum.NATURAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Building2
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON"
                      class="h-6 w-6 text-gray-600"
                    />
                    <Landmark
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.INSTITUTION"
                      class="h-6 w-6 text-gray-600"
                    />
                    <User
                      v-else
                      class="h-6 w-6 text-gray-600"
                    />
                    <!-- Default to User icon if type not specified -->
                  </div>

                  <div class="min-w-0 flex-grow">
                    <div
                      v-if="entity.entity?.entity_type === EntityTypeEnum.NATURAL_PERSON"
                      class="truncate text-sm font-semibold text-gray-600"
                    >
                      {{
                        `${entity.entity?.title || ''} ${entity.entity?.first_name || ''} ${entity.entity?.last_name || ''}`.trim() ||
                        t('unnamed')
                      }}
                    </div>
                    <div
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON"
                      class="truncate text-sm font-semibold text-gray-600"
                    >
                      {{ entity.entity?.company_name || t('unnamed') }}
                    </div>
                    <div
                      v-else-if="entity.entity?.entity_type === EntityTypeEnum.INSTITUTION"
                      class="truncate text-sm font-semibold text-gray-600"
                    >
                      {{ entity.entity?.institution_name || t('unnamed') }}
                    </div>
                    <div class="text-xs text-gray-500">
                      <span
                        v-for="(relationship, index) in entity.relationships"
                        :key="relationship.uuid"
                        class="text-gray-600"
                      >
                        {{ t(relationship.relationship_type)
                        }}{{ index < entity.relationships.length - 1 ? ', ' : '' }}
                      </span>
                    </div>
                    <div
                      v-if="!isEntityEnabled(entity)"
                      class="text-xs text-gray-500"
                    >
                      {{ t('notEnabledInExport') }}
                    </div>
                  </div>
                </div>

                <Popover
                  :open="openPopoverId === entity.uuid"
                  @update:open="
                    (val) => (val ? togglePopover(entity.uuid || '') : (openPopoverId = null))
                  "
                >
                  <PopoverTrigger
                    class="ml-2 flex-shrink-0 rounded-full p-1 hover:bg-gray-200 focus:outline-none"
                  >
                    <MoreVertical class="h-4 w-4 text-gray-500" />
                  </PopoverTrigger>
                  <PopoverContent class="w-48 p-1">
                    <div class="flex flex-col">
                      <button
                        class="flex w-full items-center rounded-md px-2 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        @click.stop="confirmDeleteEntity(entity)"
                      >
                        <Trash2 class="mr-2 h-4 w-4" />
                        {{ t('delete_party') }}
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </template>
          </div>
        </div>
        <div class="flex-1">
          <Entity
            v-if="selectedEntityRelationship"
            :entity-relationship-uuid="selectedEntityRelationship.uuid!"
            :insurance-info-enabled="insuranceInfoEnabled"
          />
          <div
            v-else
            class="flex h-full items-center justify-center text-gray-500"
          >
            {{ t('selectParty') }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete Confirmation Modal -->
  <AlertDialog
    :open="showDeleteConfirmation"
    @update:open="showDeleteConfirmation = $event"
  >
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ t('confirm_delete') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('confirm_delete_party_message') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel @click="showDeleteConfirmation = false">
          {{ t('cancel') }}
        </AlertDialogCancel>
        <AlertDialogAction
          class="bg-red-600 hover:bg-red-700"
          @click="deleteEntity()"
        >
          {{ t('delete') }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
